// Vendor imports
import React, { Component } from "react"
import { HotKeys } from "react-hotkeys"
import { connect } from "react-redux"
import Immutable from "immutable"
import classNames from "classnames"
import PropTypes from "prop-types"
import randomstring from "randomstring"
import { Row } from "react-bootstrap"
import { Table, Column, Cell } from "fixed-data-table-2"

import Tooltip from "app/static/frontend/components/Tooltip"
import Icon from "app/static/frontend/components/Icon"

import { checkPermission } from "shared/imports/permissionFunctions"
import { sendUserAction } from "shared/user-actions/user-actions-action-creators"
import { updateUserdata } from "shared/userdata/userdata-action-creators"

// Components
import DatumCell from "sheets/components/cells/DatumCell"
import DatumHeaderCell from "sheets/components/cells/DatumHeaderCell"
import { EmptyScreen } from "@/components/EmptyScreen/EmptyScreen"
import HeaderCell from "sheets/components/cells/HeaderCell"
import ResourceTableDatumHeaderCell from "sheets/components/cells/ResourceTableDatumHeaderCell"
import ResourceTableHeaderCell from "sheets/components/cells/ResourceTableHeaderCell"
import IndexCell from "sheets/components/cells/IndexCell"
import IndexHeaderCell from "sheets/components/cells/IndexHeaderCell"
import SheetCell from "sheets/components/cells/SheetCell"
import { EmptyState } from "quorumdesign"
import MathFooter from "sheets/components/MathFooter"

// redux
import { sheetConnection } from "app/static/frontend/sheets/selectors/sheet-selectors"
import { calculateIndexColumnWidth } from "sheets/helper-functions"

// helper functions
import sheetConstants from "sheets/constants"
import { swalContents, EMPTY_STATE_NO_RESULTS } from "sheets/components/helpTexts"

// styles
import "app/static/stylus/sheet.styl"
import "app/static/stylus/ResourceTable.styl"
import "fixed-data-table-2/dist/fixed-data-table.css"

const NoResults = (props) => {
    if (props?.dataType === DjangIO.app.models.QuorumDataType.bulk_sms.value) {
        return (
            <div className="no-results-container">
                <EmptyScreen
                    icon="comment"
                    iconFamily="far"
                    message="Try removing any filters or refreshing"
                    title="No results"
                />
            </div>
        )
    } else {
        return (
            <Row id="no-results" className={classNames({ "has-map": props.show_map })}>
                <EmptyState
                    empty_state_paragraph={props.noResultsMessage || EMPTY_STATE_NO_RESULTS.empty_state_paragraph}
                    empty_state_title={EMPTY_STATE_NO_RESULTS.empty_state_title}
                    icon={props.show_map ? undefined : EMPTY_STATE_NO_RESULTS.icon}
                    iconFamily={EMPTY_STATE_NO_RESULTS.iconFamily}
                />
            </Row>
        )
    }
}

export class Sheet extends Component {
    componentDidMount() {
        // Sends user actions when a sheet is viewed.
        // Do not send user actions when `Userdata.id` is undefined, because it is a public sheet
        if (Userdata && Userdata.id) {
            this.props.sendUserAction(
                DjangIO.app.useractions.types.UserActionType.sheet_viewed,
                DjangIO.app.models.QuorumDataType.by_value(this.props.dataType).plural,
                null,
                this.props.dataType,
            )
        }
    }

    infiniteScroll = (scrollLeft, scrollTop) => {
        // if we're currently loading more items already
        if (this.props.loading) {
            return
        }

        // the height of the table is determined by the number of
        // rows currently in memory times the height of each row
        const tableHeight = this.props.rowHeight * this.props.rowData.size

        // the point at which we should load more is determined by
        // multiplying the tableScrollThreshold (a percentage of the
        // table that has to be scrolled) by the total height of the table
        const threshold = tableHeight * this.props.tableScrollThreshold

        // if the user has scrolled through more than that
        // percent of the table
        if (scrollTop > threshold) {
            // console.log("Threshold crossed! Loading more...")
            // load the next batch of data
            this.props.loadNextInterval()
        }
    }
    resizeColumn = (width, columnId) => {
        // resize the column in the store
        this.props.resizeColumn(this.props.id, width, columnId)

        // send a request to store the resized column size
        !this.props.publicSheet && this.props.patchColumnSize(this.props.id, width, columnId)
    }
    reorderColumn = (payload) => {
        // reorders in the store
        // if called, we want to allow public sheets to reorder on the UI
        this.props.reorderColumns(this.props.id, payload)

        // send request to reorder in database
        // debounce the request from the user
        !this.props.publicSheet &&
            this.props.saveColumnOrder(
                this.props.sheetId,
                this.props.columns.map((col) => col.get("id")),
            )
    }

    // If a select is open, return false, according to the documentation
    // on fixed data table for the onVerticalScroll and onHorizontalScroll callbacks
    handleSelectScroll = () => !this.props.isSelectOpen

    renderIndexColumn() {
        return (
            <Column
                fixed
                // http://schrodinger.github.io/fixed-data-table-2/api-column.html#allowcellsrecycling
                allowCellsRecycling
                // http://schrodinger.github.io/fixed-data-table-2/api-column.html#purerendering
                pureRendering
                header={
                    <Cell
                        className={classNames("sheet-header-cell index-header-cell", {
                            "filtered-or-sorted": this.props.isFilteredOrSorted || this.props.loading,
                        })}
                    >
                        <IndexHeaderCell
                            {...this.props} // poor practice but not a priority to fix now
                            canHideColumns={false}
                        />
                    </Cell>
                }
                cell={({ rowIndex, ...cellProps }) => (
                    <IndexCell
                        canRemoveRows={this.props.canRemoveRows}
                        id={this.props.id}
                        removeRowNetworkRequestActionCreator={this.props.removeRowNetworkRequestActionCreator}
                        rowIndex={rowIndex}
                        rows={this.props.rows}
                        usesParentChildCell={
                            this.props.dataType === DjangIO.app.models.QuorumDataType.issue_management.value
                        }
                        {...cellProps}
                    />
                )}
                width={calculateIndexColumnWidth(this.props.rowData.size.toString().length, this.props.isResourceTable)}
                columnCount={this.props.columnCount}
            />
        )
    }

    // function that renders the clear filters button for the main datum column
    renderClearFiltering = () => {
        return (
            <Tooltip label="Click to Clear Filtering">
                <Icon
                    name="fa-times-circle-o"
                    onClick={() => this.props.clearFilter(this.props.id, sheetConstants.DATUM_COLUMN_INDEX)}
                />
            </Tooltip>
        )
    }

    renderDatumColumn() {
        const searching =
            this.props.searchingColumnIndexes &&
            this.props.searchingColumnIndexes.includes(sheetConstants.DATUM_COLUMN_INDEX)
        const { rowData, allRowData, rowCount } = this.props
        return (
            <Column
                allowCellsRecycling
                columnKey={sheetConstants.DATUM_COLUMN_INDEX}
                columnCount={this.props.columnCount}
                fixed={!this.props.isMobile} // only fix this column if we are on desktop
                flexGrow={this.props.isDesignSystemResourceTable ? 0 : 1}
                isResizable={this.props.isResizable}
                footer={
                    <MathFooter
                        sheetId={this.props.sheetId}
                        rowData={rowData}
                        allRowData={allRowData}
                        columnIndex={sheetConstants.DATUM_COLUMN_INDEX}
                        columnsToRender={this.props.columnsToRender}
                        rowCount={rowCount}
                    />
                }
                header={
                    <Cell className={classNames("sheet-header-cell", "datum-header-cell", { searching })}>
                        {this.props.isDesignSystemResourceTable ? (
                            <ResourceTableDatumHeaderCell
                                clearFilter={this.props.clearFilter}
                                columnIndex={sheetConstants.DATUM_COLUMN_INDEX}
                                id={this.props.id}
                                renderClearFiltering={this.renderClearFiltering}
                                sheetId={this.props.id}
                                showDatumColumn={this.props.showDatumColumn}
                                disableDatumFilter={this.props.disableDatumFilter}
                                resourceTableDatumSearchTrigger={this.props.resourceTableDatumSearchTrigger}
                            />
                        ) : (
                            <DatumHeaderCell
                                clearFilter={this.props.clearFilter}
                                columnIndex={sheetConstants.DATUM_COLUMN_INDEX}
                                id={this.props.id}
                                renderClearFiltering={this.renderClearFiltering}
                                sheetId={this.props.id}
                                showDatumColumn={this.props.showDatumColumn}
                            />
                        )}
                    </Cell>
                }
                cell={({ rowIndex, ...props }) => (
                    <DatumCell
                        columnCount={this.props.columnCount}
                        data-cy={`datum-cell-row-${rowIndex}`}
                        dataType={this.props.dataType}
                        datum={this.props.rowData.get(rowIndex)}
                        datumUrlOverride={this.props.datumUrlOverride}
                        disableRowLink={this.props.disableRowLink}
                        optionalPreSegueFunction={this.props.optionalPreSegueFunction}
                        params={this.props.params}
                        {...props} // TODO see if we can remove this potential override prop dependence
                        hasProjectManagementPermission={checkPermission(
                            DjangIO.app.models.PermissionType.project_management,
                        )}
                        id={this.props.id}
                        isMobile={this.props.isMobile}
                        isPublicSheet={this.props.publicSheet}
                        allowDatumLink={this.props.allowDatumLink}
                        isResourceTable={this.props.isResourceTable}
                        isDesignSystemResourceTable={this.props.isDesignSystemResourceTable}
                        resourceTableDatumTooltipText={this.props.resourceTableDatumTooltipText}
                        rowIndex={rowIndex}
                        rowHeightFactor={this.props.rowHeightFactor}
                    />
                )}
                width={
                    this.props.isDesignSystemResourceTable
                        ? sheetConstants.RESOURCE_TABLE_DATUM_COLUMN_WIDTH
                        : this.props.datumColumnWidth
                }
            />
        )
    }

    // handle logic for render columns
    // unfortunately, fixed-data-table-2 requires the direct children of the table to be
    // <Column /> components
    handleRenderColumns = () => {
        return this.props.columnsToRender.map((column) => {
            // selector provides us trueColumnIndex. This is necessary because we support hiding some columns
            // from public sheets. However, under certain circumstances, the sheet uses all of the columns
            // to compute the value of other columns. The columnIndex is used further down the tree to index
            // into the correct column in the store, and needs to always correspond to the right column, regardless
            // of what columns may have been hidden
            const columnIndex = column.trueColumnIndex
            // because of library limitations, we cannot fully take advantage of selectors
            // when providing props to the Column component
            // we need to perform a few calculations here
            const { columnWidth, derived } = column
            const columnId = column.columnId || columnIndex
            const searching =
                this.props.searchingColumnIndexes && this.props.searchingColumnIndexes.includes(columnIndex)
            const sorting = this.props.sortingColumnIndex === columnIndex
            const focused = this.props.focusedColumnIndex === columnIndex
            return (
                <Column
                    // enable fixed data table optimizations
                    allowCellsRecycling
                    // when there are less columns than the size of
                    // the table, make them expand greedily to fill the space
                    flexGrow={this.props.isDesignSystemResourceTable ? 0 : 1}
                    // pass down whether we allow resizing or reordering columns for this sheet
                    isResizable={this.props.isResizable}
                    isReorderable={this.props.isReorderable}
                    columnIndex={columnIndex}
                    // this component is the return value of .map, which requires
                    // elements to have a unique key property
                    key={columnId || randomstring.generate({ length: 20 })}
                    // columnKey is the api for identifying columns internal to
                    // fixed data table.
                    columnKey={columnId}
                    // Number of columns in the sheet
                    columnCount={this.props.columnCount}
                    // the function to render cells
                    cell={(cellProps) => (
                        <SheetCell
                            {...cellProps} // these are passed down via fixed-data-table
                            id={this.props.id}
                            column={column}
                            columnCount={this.props.columnCount}
                            columnIndex={columnIndex}
                            cellStateMappingFunc={this.props.cellStateMappingFunc}
                            datum={this.props.rowData.get(cellProps.rowIndex)}
                            dataType={this.props.dataType}
                            disableRowLink={this.props.disableRowLink}
                            datumUrlOverride={this.props.datumUrlOverride}
                        />
                    )}
                    // the component to render as the header of the column
                    header={
                        <Cell
                            className={classNames("sheet-header-cell", "sheet-header-cell-color", {
                                searching,
                                sorting,
                                derived,
                                focused,
                            })}
                        >
                            {this.props.isDesignSystemResourceTable ? (
                                <ResourceTableHeaderCell id={this.props.id} columnIndex={columnIndex} />
                            ) : (
                                <HeaderCell id={this.props.id} columnIndex={columnIndex} />
                            )}
                        </Cell>
                    }
                    footer={
                        <MathFooter
                            columnIndex={columnIndex}
                            rowData={this.props.rowData}
                            columnsToRender={this.props.columnsToRender}
                            sheetId={this.props.sheetId}
                        />
                    }
                    width={columnWidth}
                    minWidth={sheetConstants.COLUMN_MIN_WIDTH}
                    maxWidth={sheetConstants.COLUMN_MAX_WIDTH}
                />
            )
        })
    }

    toggleDevTeamMode = async () => {
        console.warn("Toggle Bypass for Table") // eslint-disable-line no-console
        if (this.props.isDevTeamMode) {
            this.props.updateUserdata({
                isDevTeamMode: false,
            })
            swal(swalContents.turnOffBypass)
        } else {
            const shouldBypass = await swal(swalContents.turnOnBypass)
            if (shouldBypass) {
                this.props.updateUserdata({
                    isDevTeamMode: true,
                })
            }
        }
    }

    render() {
        const handlers = {
            toggleDevTeamMode: this.toggleDevTeamMode,
        }
        const shouldRenderIndexColumn = !this.props.isMobile && !this.props.hideIndexColumn
        return (
            <HotKeys handlers={handlers}>
                <Table
                    footerHeight={this.props.shouldDisplayMathBar ? sheetConstants.ROW_HEIGHT : 0}
                    // the width and height are added by react-cool-dimensions (ResponsiveSheet).
                    // If those props are null, when this componend is not wrapped with a responsive sheet,
                    // default to values provided by the user and then to constants.
                    width={this.props.width || sheetConstants.TABLE_WIDTH}
                    height={this.props.height || sheetConstants.TABLE_HEIGHT}
                    // more table settings
                    keyboardScrollEnabled
                    keyboardPageEnabled
                    touchScrollEnabled
                    rowHeight={this.props.rowHeight}
                    headerHeight={
                        this.props.isDesignSystemResourceTable
                            ? sheetConstants.RESOURCE_TABLE_TABLE_HEADER_HEIGHT
                            : sheetConstants.TABLE_HEADER_HEIGHT
                    }
                    // the number of rows is the number of entries
                    // in the rowData array
                    rowsCount={this.props.rowData.size}
                    columnCount={this.props.columnCount}
                    // when columns are resized or reordered, call
                    // the correcty functions
                    onColumnResizeEndCallback={this.resizeColumn}
                    onColumnReorderEndCallback={this.reorderColumn}
                    // for some reason in order for column resizing and reordering
                    // to work properly you are supposed to negate these
                    isColumnResizing={false}
                    isColumnReordering={false}
                    // when the user finishes scrolling, call the correct
                    // function to see if it is necessary to load more data
                    onScrollEnd={this.infiniteScroll}
                    onVerticalScroll={this.handleSelectScroll}
                    onHorizontalScroll={this.handleSelectScroll}
                    showScrollbarY={!this.props.isSelectOpen}
                    showScrollbarX={!this.props.isSelectOpen}
                    // if the table is filtered or sorted add a classname so that
                    // we can make it visually obvious.
                    className={classNames({ "sorted-table": this.props.isFilteredOrSorted })}
                >
                    {shouldRenderIndexColumn && this.renderIndexColumn()}

                    {!(this.props.publicSheet && !this.props.showDatumColumn) && this.renderDatumColumn()}

                    {this.handleRenderColumns()}
                </Table>
                {/* If there are no results in the table, show that there are no results*/}
                {!this.props.loading && this.props.noResults && (
                    <NoResults
                        dataType={this.props.dataType}
                        noResultsMessage={this.props.noResultsMessage}
                        show_map={this.props.show_map}
                    />
                )}
            </HotKeys>
        )
    }
}

Sheet.propTypes = {
    canAddColumns: PropTypes.bool.isRequired,
    canHideColumns: PropTypes.bool.isRequired, // allow users to hide certain columns from public sheets
    canDownload: PropTypes.bool.isRequired,
    canFitInMemory: PropTypes.bool.isRequired,
    canRemoveColumns: PropTypes.bool.isRequired,
    canRemoveRows: PropTypes.bool.isRequired,
    canRenameColumns: PropTypes.bool.isRequired,
    canRenameSheet: PropTypes.bool.isRequired,
    clearFilter: PropTypes.func.isRequired,
    clickCell: PropTypes.func.isRequired,
    columnCount: PropTypes.number,
    columnsToRender: PropTypes.array.isRequired,
    dataType: PropTypes.number.isRequired,
    datumColumnWidth: PropTypes.number,
    downloadSheet: PropTypes.func.isRequired,
    editSheet: PropTypes.func.isRequired,
    height: PropTypes.number,
    id: PropTypes.string.isRequired,
    isDevTeamMode: PropTypes.bool.isRequired,
    isFilteredOrSorted: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool,
    isReorderable: PropTypes.bool,
    isResizable: PropTypes.bool,
    isResourceTable: PropTypes.bool,
    isSelectOpen: PropTypes.bool,
    loading: PropTypes.bool.isRequired,
    loadNextInterval: PropTypes.func.isRequired,
    name: PropTypes.string.isRequired,
    noResults: PropTypes.bool.isRequired,
    noResultsMessage: PropTypes.string,
    patchColumnSize: PropTypes.func.isRequired,
    publicSheet: PropTypes.bool,
    defaultSortType: PropTypes.number,
    removeRow: PropTypes.func.isRequired,
    reorderColumns: PropTypes.func.isRequired,
    resizeColumn: PropTypes.func.isRequired,
    resourceTableDatumTooltipText: PropTypes.string,
    rowData: PropTypes.instanceOf(Immutable.List),
    allRowData: PropTypes.instanceOf(Immutable.List),
    rowCount: PropTypes.number,
    sendUserAction: PropTypes.func.isRequired,
    shouldDisplayMathBar: PropTypes.bool,
    sheetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    show_map: PropTypes.bool,
    tableScrollThreshold: PropTypes.number.isRequired,
    width: PropTypes.number,
}

Sheet.defaultProps = {
    canAddColumns: true,
    canDownload: true,
    canHideColumns: false,
    canFitInMemory: false,
    canRemoveColumns: true,
    canRemoveRows: true,
    canRenameColumns: true,
    canRenameSheet: true,
    columns: Immutable.fromJS([]),
    columnCount: 0,
    dataType: DjangIO.app.models.QuorumDataType.person.value,
    datumColumnWidth: sheetConstants.DATUM_COLUMN_WIDTH,
    isMobile: false,
    isReorderable: true,
    isResizable: true,
    isResourceTable: false,
    isSelectOpen: false,
    loading: false,
    loadNextInterval: () => {
        // intentionally blank
    },
    name: "",
    publicSheet: false,
    resourceTableDatumTooltipText: "",
    rowData: Immutable.fromJS([]),
    allRowData: Immutable.fromJS([]),
    rowCount: 0,
    rowHeight: sheetConstants.ROW_HEIGHT,
    rowFilter: {},
    width: sheetConstants.TABLE_WIDTH,
}

export default connect((state, props) => sheetConnection(state, props), { sendUserAction, updateUserdata })(Sheet)
